import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SectionHeading } from "@/components/SectionHeading";
import { KIKI } from "@/constants/testIds";
import { useT, useLang } from "@/lib/i18n";

// Designer gradient stand-ins keyed to each mentor (no stock photos — matches
// the editorial, illustrated portrait treatment used in Testimonials).
const GRADIENTS = [
  { bg: "linear-gradient(135deg,#0A0A0A 0%,#2b2b2b 100%)", ink: "#FFCC00" },
  { bg: "linear-gradient(135deg,#FFCC00 0%,#e0a800 100%)", ink: "#0A0A0A" },
  { bg: "linear-gradient(135deg,#1a1a1a 0%,#3a3a3a 100%)", ink: "#F5F5F0" },
  { bg: "linear-gradient(135deg,#f0eee6 0%,#d8d4c6 100%)", ink: "#0A0A0A" },
  { bg: "linear-gradient(135deg,#0A0A0A 0%,#453a12 100%)", ink: "#FFCC00" },
];

export function Mentors({ onEnquire }) {
  const t = useT();
  const { lang } = useLang();
  const isHi = lang === "hi";
  const list = t("mentors.list");

  return (
    <section id="mentors" data-testid={KIKI.mentors} className="relative py-16 md:py-24">
      <div className="max-w-[90rem] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 md:gap-10 items-end mb-10 md:mb-14">
          <div className="col-span-12 md:col-span-8">
            <SectionHeading
              index="05"
              overline={t("mentors.overline")}
              overlineClassName={isHi ? "font-hindi tracking-normal text-[12px]" : ""}
            />
            <motion.h2
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className={`mt-5 font-black text-4xl sm:text-5xl md:text-6xl leading-[0.98] tracking-tight ${isHi ? "font-hindi" : "font-sans"}`}
            >
              {t("mentors.titleA")}
              <span className={isHi ? "font-hindi" : "font-display italic font-normal"}>{t("mentors.titleB")}</span>
            </motion.h2>
          </div>
          <div className="col-span-12 md:col-span-4">
            <p className={`text-muted-foreground text-sm md:text-base leading-relaxed max-w-sm md:ml-auto ${isHi ? "font-hindi" : ""}`}>
              {t("mentors.lead")}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((m, i) => {
            const g = GRADIENTS[i % GRADIENTS.length];
            const initials = m.name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("");
            return (
              <motion.article
                key={m.name}
                data-testid={`${KIKI.mentorCard}-${i}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.85, delay: i * 0.07, ease: [0.16, 1, 0.3, 1] }}
                className="brochure-card card-hover bg-card border border-border flex flex-col"
              >
                <div className="flex items-center gap-4">
                  <div
                    className="relative w-16 h-16 shrink-0 rounded-full overflow-hidden border-[2.5px] border-[#FFCC00] grid place-items-center"
                    style={{ background: g.bg }}
                    aria-hidden="true"
                  >
                    <span className="font-black text-xl leading-none tracking-tight select-none" style={{ color: g.ink }}>
                      {initials}
                    </span>
                    <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-white/10" />
                  </div>
                  {m.tag && (
                    <span className="ml-auto bg-[#0A0A0A] text-white font-mono text-[10px] tracking-[0.18em] px-3 py-1.5 rounded-md">
                      {m.tag}
                    </span>
                  )}
                </div>

                <div className="pt-5">
                  <h3 className={`font-extrabold text-xl md:text-2xl leading-tight tracking-tight ${isHi ? "font-hindi" : "font-sans"}`}>{m.name}</h3>
                  <div className={`mt-1.5 text-[15px] font-semibold ${isHi ? "font-hindi" : ""}`}>{m.role}</div>
                  <div className={`label-overline text-muted-foreground mt-2 text-[10px] ${isHi ? "font-hindi tracking-normal text-[11px]" : ""}`}>{m.org}</div>
                </div>

                <p className={`mt-4 pt-4 border-t border-border text-sm text-muted-foreground leading-relaxed ${isHi ? "font-hindi" : ""}`}>
                  {m.bio}
                </p>
              </motion.article>
            );
          })}

          {/* Closing CTA cell — invites contact, keeps the grid asymmetric */}
          <motion.button
            onClick={onEnquire}
            data-testid={KIKI.mentorsCta}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.85, delay: list.length * 0.07, ease: [0.16, 1, 0.3, 1] }}
            className="brochure-card card-hover group bg-[#FFCC00] text-[#0A0A0A] flex flex-col justify-between text-left min-h-[220px]"
          >
            <span className={`label-overline text-black/60 ${isHi ? "font-hindi tracking-normal text-[12px]" : ""}`}>{t("mentors.ctaKicker")}</span>
            <span className={`font-black text-2xl md:text-3xl leading-[1.05] tracking-tight mt-6 ${isHi ? "font-hindi" : "font-sans"}`}>
              {t("mentors.ctaTitle")}
            </span>
            <span className={`mt-6 inline-flex items-center gap-2 font-semibold text-sm ${isHi ? "font-hindi" : ""}`}>
              {t("mentors.ctaLabel")}
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2} />
            </span>
          </motion.button>
        </div>
      </div>
    </section>
  );
}
